// The render loop shared by offline and online play: rigs, camera, fog-of-war texture, hud, input,
// unit rings, dynamic resolution, ?perf profiling. Everything here reads a `GameView`, never the
// concrete core `Game` — main.ts (offline) hands in a `LocalGameView` wrapping a real, locally-stepped
// `Game`; the online path (net/onlineMatch.ts) hands in a `RemoteGameView` fed by the server's snapshot
// stream. This is what "no duplicated render code" means concretely: one frame(), one hud, one input,
// one heroView per mode, not two.
//
// fx.ts now runs through here for both modes too (the fx.ts/GameView unification): net/onlineMatch.ts
// wires the exact same `createFx(scene, view, sound)` + `afterStep(rootOf) { fx.update(rootOf) }` pattern
// main.ts always used offline, retyped against `GameView`/`ViewHero` instead of core's concrete `Game`/
// `Hero`. The two REDUCED online-only stand-ins this replaced — net/remoteFx.ts, net/remoteEntities.ts,
// and the onlineFeedback.ts sound substitute — are gone; a player now sees (and hears) the same effect
// regardless of how they joined the match. See that unification's own parity report for exactly which fx
// names and persistent entity kinds match offline pixel-for-pixel and which don't (a handful of offline-
// only richnesses the wire genuinely has no data for — a projectile's homing target, a hook's chain
// anchor — degrade gracefully rather than being invented for the online case).
import * as THREE from 'three';
import type { createEngraver } from './engrave.js';
import type { buildMap } from './mapView.js';
import { LAYOUT, visionPolygon } from '../core/layout.js';
import { createRig, disposeRig, driveHero, applyAnimEvent, type HeroView } from './heroView.js';
import { createMonsterViews, driveMonsters, disposeMonsterViews } from './monsterView.js';
import { createSummonViews, driveSummons, disposeSummonViews } from './summonView.js';
import { createWolfForms, driveWolfForms, disposeWolfForms } from './wolfFormView.js';
import { createInput } from './input.js';
import { createHud, type ResultsSecondary, type VisionState } from './hud.js';
import { createUnitRings } from './hover.js';
import { createCountdown } from './countdown.js';
import { createGovernor, LEVELS } from './quality.js';
import { readSettings, resolveFogColor, resolveQuality, resolveStyle } from './settings.js';
import { browserLockApi, devLockApi } from './pointerLock.js';
import { createVirtualPointer } from './virtualPointer.js';
import type { GameView, ViewHero } from './gameView.js';
import type { Sound } from './sound.js';
import { t } from './i18n/index.js';

export type MatchViewDeps = {
  scene: THREE.Scene;
  camera: THREE.Camera;
  eng: ReturnType<typeof createEngraver>;
  stageEl: HTMLElement;
  hudEl: HTMLElement;
  canvas: HTMLCanvasElement;
  mapView: ReturnType<typeof buildMap>;
  sound: Sound;
  view: GameView;
  /** Advances local sim state for this frame and returns how much sim time passed, for the animation
      mixer to advance by exactly that much rather than by wall-clock dt (they only differ during a
      hitch — see the stage-1b port notes on why that distinction matters for one-shot clips). Offline
      runs `Game.step()` in a fixed-step accumulator here; online is a no-op returning the clamped
      wall-clock dt — there is nothing to step, the server already did, stage 2 has no prediction. */
  stepSim(dt: number): number;
  /** Called once per frame after stepSim, before rendering — both modes pass `fx.update(rootOf)` here now
      (see the module header), each `createFx()`'d against its own `view`. `rootOf` resolves a hero to its
      rendered root object (heroViews are constructed inside this function, so the caller cannot have a
      reference before the frame loop starts). */
  afterStep?(rootOf: (h: ViewHero) => THREE.Object3D): void;
  onBack: () => void;
  /** The end banner's and results screen's main button; default «В меню». */
  backLabel?: string;
  /** The results screen's second button, asked for when the screen mounts (offline «Ещё матч», online
      «Смотреть повтор» once the server named a replay); null or omitted = none. */
  resultsSecondary?: () => ResultsSecondary | null;
  /** Where the camera looks; default the player's own hero (a replay moves it freely or follows anyone). */
  cameraTarget?: () => { x: number; z: number };
  /** false: a spectator (replay) — no pointer/keyboard orders, no hover/focus rings. Default true. */
  interactive?: boolean;
  params: URLSearchParams;
};

const NO_INPUT = { targeting: null };

export function runMatchView(deps: MatchViewDeps) {
  const { scene, camera, eng, stageEl, hudEl, canvas, mapView, sound, view, stepSim, afterStep, onBack, backLabel, resultsSecondary, params } = deps;
  const interactive = deps.interactive ?? true;

  const heroViews: HeroView[] = view.heroes.map(h => ({ hero: h, rig: createRig(scene, h.def, h.team) }));
  const rootOf = (h: ViewHero) => heroViews[h.id]!.rig.root;
  const monsterViews = createMonsterViews(scene, LAYOUT.bosses.map(b => b.kind));
  const summonViews = createSummonViews();
  const wolfForms = createWolfForms();

  const visionState: VisionState = { polys: [] };
  const hud = createHud(hudEl, view, camera, stageEl, visionState, onBack, backLabel, resultsSecondary);
  const input = interactive ? createInput({
    stageEl, canvas, camera, game: view, scene, toast: hud.toast,
    stalls: mapView.stalls, onOpenShop: hud.openShop, consumePress: hud.consumeWorldPress,
  }) : null;
  const unitRings = interactive ? createUnitRings(scene, view) : null;
  const countdown = createCountdown(stageEl, k => eng.setSaturation(k));

  const muteBtn = document.createElement('button');
  muteBtn.type = 'button'; muteBtn.className = 'mute'; muteBtn.id = 'mute';
  const syncMute = () => { muteBtn.textContent = sound.muted ? t('match.soundOff') : t('match.soundOn'); muteBtn.setAttribute('aria-pressed', String(!sound.muted)); };
  const onMuteClick = () => { sound.setMuted(!sound.muted); syncMute(); };
  const onMuteKey = (e: KeyboardEvent) => { if (e.code === 'KeyM') { sound.setMuted(!sound.muted); syncMute(); } };
  muteBtn.addEventListener('click', onMuteClick);
  window.addEventListener('keydown', onMuteKey);
  syncMute();
  stageEl.appendChild(muteBtn);

  const OFFSET = new THREE.Vector3(0, 21, 15.5);
  let zoom = 1;
  const player = view.player as ViewHero;
  const target = deps.cameraTarget ?? (() => player.pos);
  const focus = new THREE.Vector3(target().x, 0, target().z);
  const onWheel = (e: WheelEvent) => { e.preventDefault(); zoom = Math.max(0.55, Math.min(2.2, zoom * (e.deltaY > 0 ? 1.1 : 0.9))); };
  stageEl.addEventListener('wheel', onWheel, { passive: false });

  function syncPoints() {
    view.points.forEach((p, i) => {
      const v = mapView.points[i]!, k = Math.round(Math.abs(p.progress) * 20);
      v.arcA.visible = p.progress > 0.02;
      v.arcB.visible = p.progress < -0.02;
      if (v.arcA.visible) v.arcA.geometry = v.arcs.a[k]!;
      if (v.arcB.visible) v.arcB.geometry = v.arcs.b[k]!;
    });
  }

  const visCanvas = document.createElement('canvas');
  visCanvas.width = 512;
  visCanvas.height = Math.round(512 * LAYOUT.bounds.z / LAYOUT.bounds.x);
  const visCtx = visCanvas.getContext('2d')!;
  const visTex = new THREE.CanvasTexture(visCanvas);
  visTex.generateMipmaps = false;
  visTex.minFilter = THREE.LinearFilter;
  eng.setVision(visTex, LAYOUT.bounds.x, LAYOUT.bounds.z);
  let visAt = -1;
  function drawVision() {
    if (view.t - visAt < 0.1 && visAt >= 0) return;
    visAt = view.t;
    visionState.polys = view.visionSources(player.team).map(src => visionPolygon(src));
    const W = visCanvas.width, H = visCanvas.height, k = W / (2 * LAYOUT.bounds.x);
    visCtx.fillStyle = '#000';
    visCtx.fillRect(0, 0, W, H);
    visCtx.fillStyle = '#fff';
    for (const poly of visionState.polys) {
      visCtx.beginPath();
      poly.forEach(([x, z], i) => { const px = (x + LAYOUT.bounds.x) * k, py = (z + LAYOUT.bounds.z) * k; i ? visCtx.lineTo(px, py) : visCtx.moveTo(px, py); });
      visCtx.closePath();
      visCtx.fill();
    }
    visTex.needsUpdate = true;
  }

  function syncVisibility() {
    for (const hv of heroViews) {
      const seen = view.visibleTo(player.team, hv.hero);
      hv.rig.root.visible = seen;
    }
  }

  function driveAnims() {
    for (const e of view.events) {
      if (e.type !== 'anim') continue;
      const hv = heroViews[e.hero];
      if (hv) applyAnimEvent(hv.rig, e);
    }
  }

  const clock = new THREE.Clock();
  const PERF = params.has('perf');
  const perf = { acc: {} as Record<string, number>, frames: 0, t0: performance.now(), el: null as HTMLElement | null, last: null as any };
  if (PERF) {
    perf.el = document.createElement('pre');
    perf.el.style.cssText = 'position:absolute;right:10px;top:84px;margin:0;padding:6px 8px;font:11px/1.35 monospace;background:rgba(239,227,198,.92);color:#2c1e12;border:1px solid #3b2a1c;pointer-events:none;z-index:5';
    stageEl.appendChild(perf.el);
  }
  function timed(name: string, fn: () => void) {
    if (!PERF) return fn();
    const t = performance.now(); fn(); perf.acc[name] = (perf.acc[name] || 0) + performance.now() - t;
  }

  // Quality ladder (quality.ts): always full quality by default — the automatic governor can't yet tell a
  // vsync-noisy CPU-bound machine from a GPU-bound one, and lowers quality for nothing there. It runs only
  // with ?quality=auto. The settings screen's level, or ?quality=N over it, pins a level;
  // window.inkbound.quality.force(N | null) pins at runtime.
  const settings = readSettings();
  const { force: forcedQuality, governor: autoQuality } = resolveQuality(settings, params);
  eng.setFogColor(resolveFogColor(settings, params));
  eng.setStyle(resolveStyle(settings, params));
  const governor = createGovernor();
  // Pointer capture (virtualPointer.ts) for a player, never a spectator; ?vcursor=1 fakes the lock for
  // browsers without one (headless), on whatever the setting says.
  const devCursor = params.get('vcursor') === '1';
  const vpointer = interactive ? createVirtualPointer({
    stageEl, enabled: devCursor || settings.lockCursor,
    api: devCursor ? devLockApi(window) : browserLockApi(document), deltaFromClient: devCursor,
    stats: params.has('vcursor-stats'),  // window.inkbound.vcursor.stats()
  }) : null;
  let applied = -1, lastFrameAt = performance.now();  // -1: the first frame resets whatever a previous match left on the page-lifetime engraver
  function applyQuality() {
    const lv = governor.level;
    if (lv === applied) return;
    applied = lv;
    eng.setRenderScale(LEVELS[lv]!.scale);
    eng.setShadowLow(LEVELS[lv]!.shadowLow);
  }
  const quality = {
    get level() { return governor.level; },
    force(l: number | null) { governor.force(l); applyQuality(); },
  };
  if (forcedQuality !== null) quality.force(forcedQuality);
  function adaptQuality() {
    const now = performance.now();
    if (autoQuality) governor.sample(now - lastFrameAt, now, document.visibilityState === 'visible');
    lastFrameAt = now;
    applyQuality();
  }

  let stopped = false;
  // Each stage fails alone: one throwing subsystem (say fx on a malformed online state) must not also skip
  // clearEvents()/render(), or the image freezes and the same events replay every frame. Counted per stage
  // (window.inkbound.stageErrors, ?perf overlay) and logged at most once per LOG_EVERY per stage, so a
  // throw repeating at 60/s neither floods the console nor goes quiet after its first line.
  const LOG_EVERY = 5000;
  const stageErrors: Record<string, number> = {};
  const loggedAt: Record<string, number> = {};
  function stageFailed(name: string, err: unknown) {
    stageErrors[name] = (stageErrors[name] ?? 0) + 1;
    const now = performance.now();
    if (now - (loggedAt[name] ?? -Infinity) < LOG_EVERY) return;
    loggedAt[name] = now;
    console.error(`matchView frame(): ${name} threw (${stageErrors[name]} so far) — skipped for this frame`, err);
  }
  function stage(name: string, fn: () => void) {
    try { timed(name, fn); } catch (err) { stageFailed(name, err); }
  }
  function frame() {
    if (stopped) return;
    // A throw anywhere in one frame's work must never be the last frame: `requestAnimationFrame` does not
    // reschedule itself when its callback throws, so an uncaught exception here used to freeze the tab on
    // a static image with no input, no error surfaced beyond the console, and no reconnect — a stage-3
    // review of a real crash (a resync mid-dash) found this the same day it found that crash's own root
    // cause. This guard is the backstop for whatever the NEXT bug of that shape is, not a fix for any one
    // of them: it skips the rest of this frame's work and tries again next frame, on the assumption that a
    // transient failure at frame N doesn't also break frame N+1 — which held for every failure mode this
    // codebase has actually hit (a bad snapshot, a stale prediction) since none of them are self-repeating.
    try {
      const t0 = performance.now();
      adaptQuality();
      const dt = Math.min(clock.getDelta(), 0.25);
      let simDt = 0;
      stage('sim', () => { simDt = stepSim(dt); });
      stage('view', () => {
        driveAnims();
        for (const hv of heroViews) driveHero(view, hv, view.t, simDt);
        driveWolfForms(wolfForms, heroViews, view, camera, view.t, dt);
        driveMonsters(monsterViews, view, simDt);
        driveSummons(summonViews, scene, view, camera, dt);
        syncPoints(); syncVisibility(); drawVision();
      });
      if (input && unitRings) stage('input', () => {
        vpointer?.update(!view.winner);  // an ended match lets the cursor go: the results screen is a menu
        const monsters = view.monsters;
        input.update(monsters);
        unitRings.update(input.hovered, input.focus, monsters);
        vpointer?.shape();  // after input.ts set this frame's data-cursor
      });
      const aim = target();
      focus.lerp(new THREE.Vector3(aim.x, 0, aim.z), 1 - Math.exp(-dt * 6));
      camera.position.copy(focus).addScaledVector(OFFSET, zoom);
      camera.lookAt(focus.x, 0.8, focus.z);
      eng.followSun(focus.x, focus.z);
      stage('fx', () => afterStep?.(rootOf));
      stage('hud', () => { hud.update(input ?? NO_INPUT); countdown.update(view.countdown, dt); });
      view.clearEvents();
      stage('render', () => eng.render());
      if (PERF) {
        perf.acc.frame = (perf.acc.frame || 0) + performance.now() - t0;
        perf.frames++;
        const el = performance.now() - perf.t0;
        if (el > 1000) {
          const ms = (k: string) => ((perf.acc[k] ?? 0) / perf.frames).toFixed(2);
          const p = eng.stats.passes;
          perf.last = { fps: perf.frames * 1000 / el, quality: governor.level, ...Object.fromEntries(Object.keys(perf.acc).map(k => [k, +ms(k)])), calls: eng.stats.calls, tris: eng.stats.tris, passes: { ...p } };
          (perf.el as HTMLElement).textContent = `fps ${(perf.frames * 1000 / el).toFixed(1)}  quality ${governor.level} (scale ${LEVELS[governor.level]!.scale.toFixed(2)})\ncpu/frame ${ms('frame')} ms\n  sim ${ms('sim')}  view ${ms('view')}\n  fx ${ms('fx')}  hud ${ms('hud')}  input ${ms('input')}\n  render(cpu) ${ms('render')}\ndraw calls ${eng.stats.calls}  tris ${(eng.stats.tris / 1000).toFixed(0)}k\n  ${Object.entries(p).map(([k, v]) => k + ' ' + v).join('  ')}${Object.keys(stageErrors).length ? `\nerrors ${Object.entries(stageErrors).map(([k, v]) => k + ' ' + v).join('  ')}` : ''}`;
          perf.acc = {}; perf.frames = 0; perf.t0 = performance.now();
        }
      }
    } catch (err) {
      stageFailed('frame', err);
    }
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
  (window as any).inkbound = { ...(window as any).inkbound, view, scene, camera, eng, perf, quality, stageErrors, vcursor: vpointer };

  // Tears down everything this call added to shared, page-lifetime state (scene, stageEl, window) — without
  // it, a second match in the same page session (the online lobby's "play again" loop never reloads the
  // page) renders every hero twice, fires every key handler twice, and leaves the aim/hover/mute UI from
  // the dead match sitting in the DOM. `stageEl`/`canvas`/`scene`/`window` are the same objects across
  // matches; only what this function itself created needs undoing.
  function stop() {
    if (stopped) return;
    stopped = true;
    input?.destroy();
    vpointer?.destroy();
    hud.destroy();
    unitRings?.destroy();
    countdown.destroy();
    stageEl.removeEventListener('wheel', onWheel);
    window.removeEventListener('keydown', onMuteKey);
    muteBtn.removeEventListener('click', onMuteClick);
    muteBtn.remove();
    if (perf.el) perf.el.remove();
    visTex.dispose();
    disposeWolfForms(wolfForms, heroViews);
    for (const hv of heroViews) disposeRig(scene, hv.rig);
    disposeMonsterViews(scene, monsterViews);
    disposeSummonViews(summonViews, scene);
  }

  /** Redraw the fog on the next frame even though `view.t` has not moved (a paused replay seeked or
      switched whose vision it shows). */
  function invalidateVision() { visAt = -1; }

  return { stop, invalidateVision };
}
